import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { Box, styled, Theme } from '@mui/material';
import { ConversationMessageDispatchStatus } from '@provision/conversation-types';
import type { ConversationMessageDispatch } from '@provision/entity-types';
import { getEffectiveDispatchStatus } from './dispatch-status.utils';

interface DispatchStatusIconProps {
  dispatches: Pick<
    ConversationMessageDispatch,
    'dispatchStatus' | 'createdAt' | 'updatedAt'
  >[];
}

export const DISPATCH_STATUS_ICON_SIZE = '1.6rem';

const Root = styled(Box)(() => ({
  width: DISPATCH_STATUS_ICON_SIZE,
  height: DISPATCH_STATUS_ICON_SIZE,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  cursor: 'default',
}));

// helper to map status → theme color
const getStatusColor = (status: ConversationMessageDispatchStatus) => {
  const map: Partial<Record<ConversationMessageDispatchStatus, (theme: Theme) => string>> = {
    [ConversationMessageDispatchStatus.Seen]: (theme) => theme.palette.success.light,
    [ConversationMessageDispatchStatus.Dispatched]: (theme) => theme.palette.primary.main,
    [ConversationMessageDispatchStatus.Dispatching]: (theme) => theme.palette.grey[500],
  };
  return map[status] ?? (() => 'inherit');
};

export const DispatchStatusIcon = ({ dispatches }: DispatchStatusIconProps) => {
  const uniqueStatuses = Array.from(new Set(dispatches.map(getEffectiveDispatchStatus)));

  // if any dispatch failed, surface the error icon — failure takes priority
  if (uniqueStatuses.includes(ConversationMessageDispatchStatus.Failed)) {
    return (
      <Root>
        <CloseIcon fontSize="inherit" color="error" />
      </Root>
    );
  }

  // pick single vs. "all" icon
  const SingleIcon = dispatches.length === 1 ? CheckIcon : DoneAllIcon;

  // single-status case
  if (uniqueStatuses.length === 1) {
    const status = uniqueStatuses[0];

    return (
      <Root>
        <SingleIcon fontSize="inherit" sx={{ color: getStatusColor(status) }} />
      </Root>
    );
  }

  // multi-status (two overlapping checks)
  return (
    <Root sx={{ position: 'relative', p: 0 }}>
      <CheckIcon
        fontSize="inherit"
        sx={{
          position: 'absolute',
          left: 0,
          width: '1rem',
          height: '1rem',
          color: getStatusColor(ConversationMessageDispatchStatus.Seen),
        }}
      />
      <CheckIcon
        fontSize="inherit"
        sx={{
          position: 'absolute',
          left: 4.5,
          width: '1rem',
          height: '1rem',
          color: getStatusColor(ConversationMessageDispatchStatus.Dispatched),
        }}
      />
    </Root>
  );
};
